import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import SimpleFormDialog from "components/dialogs/SimpleFormDialog";
import { SelectedTemplateQuery } from "routes/dashboard/DashboardPageRoot";
import { Box, Chip, Paper } from "@mui/material";
import { getDashboardNavigatePath, parsePageParams } from "./DashboardNavigationUtils";
import { useNavigate } from "react-router-dom";

export interface DashboardTemplateQuerySelectorDialogProps {
  allTemplateQueries: SelectedTemplateQuery[];
  selectedTemplateQueries: SelectedTemplateQuery[];
  onClose: () => void;
}

export default function DashboardTemplateQuerySelectorDialog(props: DashboardTemplateQuerySelectorDialogProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const { allTemplateQueries, onClose } = props;
  const navigate = useNavigate();

  const [selectedTemplateQueries, setSelectedTemplateQueries] = React.useState<SelectedTemplateQuery[]>(props.selectedTemplateQueries);

  const displayRows = useMemo(() => {
    return allTemplateQueries.map((tq) => {
      const isSelected = selectedTemplateQueries.find((stq) => stq.index === tq.index) !== undefined;

      const color = isSelected ? `color(from ${tq.color} srgb r g b / 0.6)` : 'auto';

      return (
        <Box key={tq.index} sx={{display:'inline-block'}}>
          <Chip
            sx={{ mb: 1, mr: 1, backgroundColor: color }}
            label={tq.query.name}
            onClick={() => {
              const alreadySelected = selectedTemplateQueries.find((stq) => stq.index === tq.index) !== undefined;
              if (alreadySelected) {
                setSelectedTemplateQueries(selectedTemplateQueries.filter((stq) => stq.index !== tq.index));
              } else {
                setSelectedTemplateQueries([...selectedTemplateQueries, tq]);
              }
            }}
            />
        </Box>
      );
    });
  }, [allTemplateQueries, selectedTemplateQueries]);

return (
  <SimpleFormDialog
    isOpen={true}
    onCancel={() => {
      onClose();
    }}
    onConfirm={()=>{
      const pathParts = parsePageParams();

      if (selectedTemplateQueries.length) {
        pathParts.templateQueryIndices = selectedTemplateQueries.map((tq) => tq.index);
      } else {
        pathParts.templateQueryIndices = [0];
      }

      const navigatePath = getDashboardNavigatePath(pathParts);
      navigate(navigatePath, { replace: true });
      onClose();
    }}
    onSelectAll={() => setSelectedTemplateQueries(allTemplateQueries)}
    onDeselectAll={() => setSelectedTemplateQueries([])}
    title={i18n.t("dashboard.select-template-queries")}
  >
    <Paper sx={{ textAlign: 'center', p: 2, pb: 1 }}>
      {displayRows}
    </Paper>
  </SimpleFormDialog>
);
}